import { Box, Burger, Group } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Link } from "@tanstack/react-router";
import { Suspense, lazy } from "react";

import { useAuth } from "@/api/resources/auth/hooks";
import AppAdmin from "@/components/AppAdmin";
import AppLogo from "@/components/AppLogo";
import AppStatusBanner from "@/components/AppStatus/AppStatusBanner";
import NavbarDrawer from "@/components/Navbar/NavbarDrawer";
import NavbarUserMenu from "@/components/Navbar/NavbarUserMenu";
import { SearchDialogTriggerField } from "@/components/Search/SearchDialogTrigger";
import { SYSTEM_COLORS } from "@/configs/colorScheme";
import { LAYOUT_HEADER_HEIGHT } from "@/configs/layout";
import { AppLayout, useAppLayout } from "@/hooks/useAppLayout";
import { useIsMobile } from "@/hooks/useIsMobile";

const SearchDialog = lazy(() => import("@/components/Search/SearchDialog"));
const OnboardingAssistant = lazy(() => import("@/components/Onboarding/Assistant/OnboardingAssistant"));
const WorkspaceSelectorTree = lazy(() => import("@/components/Workspace/WorkspaceSelectorTree"));
const AppFab = lazy(() => import("@/components/AppFab/AppFab"));

const LayoutHeader = () => {
  const { appLayout } = useAppLayout();
  const { isAuthenticated } = useAuth();
  const { isMobile, isTablet } = useIsMobile();
  const [opened, { close, toggle }] = useDisclosure();

  const isNavlessLayout = appLayout === AppLayout.Navless;
  const isDefaultLayout = appLayout === AppLayout.Default;

  return (
    <Group justify="space-between" bg="primary" wrap="nowrap" h={LAYOUT_HEADER_HEIGHT}>
      <Link to="/">
        <AppLogo />
      </Link>

      {isAuthenticated && (
        <>
          {!isMobile && !isNavlessLayout && (
            <Suspense>
              <SearchDialog
                dialogTriggerButton={(dialogTriggerProps) => (
                  <Box maw="20rem" flex={1}>
                    <SearchDialogTriggerField size="sm" {...dialogTriggerProps} />
                  </Box>
                )}
              />
            </Suspense>
          )}

          <Group wrap="nowrap" gap="xs">
            {!isTablet && !isNavlessLayout && (
              <Suspense>
                <Box w="24rem">
                  <WorkspaceSelectorTree />
                </Box>
              </Suspense>
            )}
            <AppStatusBanner />
            <AppAdmin />
            {isTablet && <Burger opened={opened} onClick={toggle} color={SYSTEM_COLORS.secondary} />}
            <NavbarUserMenu />
          </Group>

          {isDefaultLayout && (
            <Suspense>
              <OnboardingAssistant />
            </Suspense>
          )}

          {isDefaultLayout && (
            <Suspense>
              <AppFab />
            </Suspense>
          )}

          <NavbarDrawer opened={opened} onClose={close} />
        </>
      )}
    </Group>
  );
};

export default LayoutHeader;
